/**
 * Language Service
 *
 * Service for managing user interface language, including detection,
 * validation, and persistence. Handles localStorage with fallback for
 * private browsing mode.
 *
 * @validates Requirements 1.1, 1.2, 1.3, 1.4, 2.1, 2.2, 2.3, 2.4
 */

/**
 * Supported locale codes following ISO 639-1
 */
export type LocaleCode = 'fr' | 'en' | 'es' | 'de' | 'pt' | 'it';

/**
 * Storage key for language preference
 */
export const LANGUAGE_STORAGE_KEY = 'spellion_language';

/**
 * Default fallback language
 */
export const DEFAULT_LANGUAGE: LocaleCode = 'en';

/**
 * Supported languages configuration
 */
export const SUPPORTED_LANGUAGES: ReadonlyArray<{
  code: LocaleCode;
  name: string;
  nativeName: string;
  flag: string;
}> = [
  { code: 'fr', name: 'French', nativeName: 'Français', flag: '🇫🇷' },
  { code: 'en', name: 'English', nativeName: 'English', flag: '🇬🇧' },
  { code: 'es', name: 'Spanish', nativeName: 'Español', flag: '🇪🇸' },
  { code: 'de', name: 'German', nativeName: 'Deutsch', flag: '🇩🇪' },
  { code: 'pt', name: 'Portuguese', nativeName: 'Português', flag: '🇵🇹' },
  { code: 'it', name: 'Italian', nativeName: 'Italiano', flag: '🇮🇹' },
] as const;

/**
 * Languages visible in the selector (subset of SUPPORTED_LANGUAGES)
 * Other languages are kept for future use but not shown to users
 */
export const VISIBLE_LANGUAGES = SUPPORTED_LANGUAGES.filter(
  (lang) => lang.code === 'fr' || lang.code === 'en'
);

/**
 * Safe localStorage wrapper for private browsing compatibility.
 * Returns a no-op storage implementation when localStorage is unavailable.
 */
function safeLocalStorage(): Pick<Storage, 'getItem' | 'setItem' | 'removeItem'> {
  try {
    const testKey = '__storage_test__';
    localStorage.setItem(testKey, testKey);
    localStorage.removeItem(testKey);
    return localStorage;
  } catch {
    // Return a no-op storage for private browsing or quota exceeded
    return {
      getItem: () => null,
      setItem: () => {},
      removeItem: () => {},
    };
  }
}

/**
 * Checks if a value is a valid LocaleCode
 *
 * @param value - The value to check
 * @returns True if the value is a valid LocaleCode
 */
export function isValidLocale(value: unknown): value is LocaleCode {
  return value === 'fr' || value === 'en' || value === 'es' || value === 'de' || value === 'pt' || value === 'it';
}

/**
 * Detects the browser's preferred language from navigator.language
 * Returns 'fr' if French is detected, otherwise 'en' (universal fallback)
 *
 * @param browserLocale - Optional override for testing purposes
 * @returns The detected LocaleCode
 *
 * @validates Requirements 1.2, 1.3
 */
export function detectBrowserLocale(browserLocale?: string): LocaleCode {
  const locale = browserLocale ?? (typeof navigator !== 'undefined' ? navigator.language : '');
  const lowerLocale = locale.toLowerCase();
  
  // Only French gets French, everything else defaults to English (universal)
  if (lowerLocale.startsWith('fr')) {
    return 'fr';
  }
  
  return 'en';
}

/**
 * Retrieves the stored language preference from localStorage
 * Returns null if no valid preference exists or if localStorage is unavailable
 *
 * @returns The stored LocaleCode or null if not found/invalid
 *
 * @validates Requirements 2.2
 */
export function getStoredLanguage(): LocaleCode | null {
  const storage = safeLocalStorage();
  const stored = storage.getItem(LANGUAGE_STORAGE_KEY);
  
  if (stored === null) {
    return null;
  }
  
  if (isValidLocale(stored)) {
    return stored;
  }
  
  // Invalid value stored - clear it
  storage.removeItem(LANGUAGE_STORAGE_KEY);
  return null;
}

/**
 * Stores the language preference to localStorage
 *
 * @param locale - The LocaleCode to store
 *
 * @validates Requirements 2.1, 1.4
 */
export function storeLanguage(locale: LocaleCode): void {
  const storage = safeLocalStorage();
  storage.setItem(LANGUAGE_STORAGE_KEY, locale);
}

/**
 * Determines the initial language on application startup.
 * Priority:
 *   1. Valid localStorage value
 *   2. Browser locale detection
 *
 * If an invalid value exists in localStorage, it will be overwritten
 * with the detected valid locale.
 *
 * @param browserLocale - Optional override for testing purposes
 * @returns The initial LocaleCode to use
 *
 * @validates Requirements 2.2, 2.3, 2.4, 1.2, 1.3, 1.4
 */
export function getInitialLanguage(browserLocale?: string): LocaleCode {
  const storedLanguage = getStoredLanguage();
  
  // Priority 1: Valid stored language
  if (storedLanguage !== null) {
    return storedLanguage;
  }
  
  // Priority 2: Browser detection (and store the result)
  const detectedLanguage = detectBrowserLocale(browserLocale);
  storeLanguage(detectedLanguage);
  
  return detectedLanguage;
}
